'use client'

import {
	AlertDialog,
	AlertDialogAction,
	AlertDialogCancel,
	AlertDialogContent,
	AlertDialogDescription,
	AlertDialogFooter,
	AlertDialogHeader,
	AlertDialogTitle,
} from '@/components/ui/alert-dialog'
import { cn } from '@/lib/utils'
import { buttonVariants } from '@/components/ui/button'
import type Stripe from 'stripe'

type PlanInfo = {
	name: string
	price: number
	interval: Stripe.Price.Recurring.Interval
}

type Props = {
	isOpen: boolean
	currentPlan: PlanInfo
	newPlan: PlanInfo | null // null = cancellation
	onConfirm: () => void
	onCancel: () => void
}

const formatPrice = (price: number, interval: Stripe.Price.Recurring.Interval) => {
	const intervalLabel = interval === 'year' ? 'year' : interval === 'month' ? 'month' : interval
	return `$${price}/${intervalLabel} per user`
}

const ConfirmPlanChangeDialog = ({ isOpen, currentPlan, newPlan, onConfirm, onCancel }: Props) => {
	const isCancellation = newPlan === null
	const isUpgrade = newPlan !== null && newPlan.price > currentPlan.price

	const title = isCancellation ? 'Cancel your subscription?' : 'Change your plan?'
	const description = isCancellation
		? `You're about to cancel your ${currentPlan.name} plan.`
		: 'Review the changes to your subscription.'

	return (
		<AlertDialog open={isOpen} onOpenChange={open => !open && onCancel()}>
			<AlertDialogContent>
				<AlertDialogHeader>
					<AlertDialogTitle>{title}</AlertDialogTitle>
					<AlertDialogDescription>{description}</AlertDialogDescription>
				</AlertDialogHeader>

				<div className="flex items-center gap-4">
					<div className="flex-1 rounded-lg border border-border p-3 text-center">
						<div className="text-xs uppercase tracking-wide text-muted-foreground">Current Plan</div>
						<div className="mt-1 text-base font-semibold">{currentPlan.name}</div>
						<div className="text-sm text-muted-foreground">
							{formatPrice(currentPlan.price, currentPlan.interval)}
						</div>
					</div>

					<div className="text-xl text-muted-foreground">→</div>

					{isCancellation ? (
						<div className="flex-1 rounded-lg border-2 border-border bg-muted p-3 text-center">
							<div className="text-xs uppercase tracking-wide text-muted-foreground">
								After Cancellation
							</div>
							<div className="mt-1 text-base font-semibold text-muted-foreground">Free</div>
							<div className="text-sm text-muted-foreground">$0.00/mo</div>
						</div>
					) : (
						<div className="flex-1 rounded-lg border-2 border-hitemup-green bg-green-50 p-3 text-center">
							<div className="text-xs uppercase tracking-wide text-muted-foreground">New Plan</div>
							<div className="mt-1 text-base font-semibold">{newPlan.name}</div>
							<div className="text-sm font-medium text-hitemup-green">
								{formatPrice(newPlan.price, newPlan.interval)}
							</div>
						</div>
					)}
				</div>

				{isCancellation ? (
					<div className="flex items-start gap-2 rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-800">
						<span className="shrink-0">⚠️</span>
						<span>
							Your subscription will remain active until the end of your current billing period. After
							that, you&apos;ll lose access to premium features.
						</span>
					</div>
				) : isUpgrade ? (
					<div className="flex items-start gap-2 rounded-lg border border-yellow-300 bg-yellow-50 p-3 text-sm text-yellow-800">
						<span className="shrink-0">⚠️</span>
						<span>
							You will be charged a prorated amount immediately for the remainder of your current billing
							period.
						</span>
					</div>
				) : (
					<div className="flex items-start gap-2 rounded-lg border border-blue-300 bg-blue-50 p-3 text-sm text-blue-800">
						<span className="shrink-0">ℹ️</span>
						<span>
							The unused time on your current plan will be credited to your account and applied toward
							future payments.
						</span>
					</div>
				)}

				<AlertDialogFooter>
					<AlertDialogCancel onClick={onCancel}>{isCancellation ? 'Keep Plan' : 'Cancel'}</AlertDialogCancel>
					<AlertDialogAction
						onClick={onConfirm}
						className={cn(
							isCancellation
								? buttonVariants({ variant: 'destructive' })
								: 'bg-hitemup-green hover:bg-hitemup-green/90',
						)}
					>
						{isCancellation ? 'Cancel Subscription' : 'Confirm Change'}
					</AlertDialogAction>
				</AlertDialogFooter>
			</AlertDialogContent>
		</AlertDialog>
	)
}

export default ConfirmPlanChangeDialog
